import { useTranslation } from 'react-i18next'
import { mdiDownload, mdiRefresh } from '@mdi/js'
import { ensureAuthCookie } from '../../api/client'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'

interface PreviewErrorProps {
  contentUrl: string
  name: string
  onRetry: () => void
}

/**
 * The preview could not be loaded (the file answered non-2xx even after a
 * token refresh, or the media element failed). Says so, and offers a retry
 * and the download as a fallback. Never shows the server's error body.
 */
export function PreviewError({ contentUrl, name, onRetry }: PreviewErrorProps) {
  const { t } = useTranslation('documents')
  return (
    <div className="p-4 flex flex-col items-start gap-2" data-testid="document-preview-error">
      <p className="text-muted-foreground text-sm">{t('detail.preview_load_error')}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onRetry} data-testid="document-preview-retry">
          <AppIcon path={mdiRefresh} size="xs" className="mr-1" />
          {t('detail.preview_retry')}
        </Button>
        <Button asChild variant="ghost" size="sm">
          <a
            href={contentUrl}
            download={name}
            onClick={() => { void ensureAuthCookie() }}
            data-testid="document-preview-download"
          >
            <AppIcon path={mdiDownload} size="xs" className="mr-1" />
            {t('detail.download')}
          </a>
        </Button>
      </div>
    </div>
  )
}

export function PreviewLoading() {
  const { t } = useTranslation('documents')
  return (
    <p className="p-4 text-muted-foreground text-sm" data-testid="document-preview-loading">
      {t('detail.preview_loading')}
    </p>
  )
}
